import { turbo } from '~/utils/colormap';
import { DvfDisplayRangeMm } from '~/domain/dvf/dvfDisplayRange';

export const MIN_DISPLACEMENT_MM = 0.3;

export const ARROW_CELLS_AT_FULL_LENGTH = 1;

export function arrowLengthScale(
    fullLengthAtMm: number,
    spacingMm: number
): number {
    return fullLengthAtMm > 0
        ? (ARROW_CELLS_AT_FULL_LENGTH * spacingMm) / fullLengthAtMm
        : 1;
}

const MIN_ARROW_FRACTION = 0.45;

export function arrowDrawnLengthMm(
    magnitudeMm: number,
    lengthScale: number,
    spacingMm: number
): number {
    const fullLengthMm = ARROW_CELLS_AT_FULL_LENGTH * spacingMm;
    const fraction = Math.min(1, (magnitudeMm * lengthScale) / fullLengthMm);
    return (
        fullLengthMm *
        (MIN_ARROW_FRACTION + (1 - MIN_ARROW_FRACTION) * fraction)
    );
}

export const N_BUCKETS = 8;

const RAMP_FLOOR = 0.12;

const BUCKET_COLORS: string[] = Array.from({ length: N_BUCKETS }, (_, k) => {
    const [r, g, b] = turbo(
        RAMP_FLOOR + (1 - RAMP_FLOOR) * (k / (N_BUCKETS - 1))
    );
    return `rgb(${r},${g},${b})`;
});

export function magnitudeBucket(
    magnitudeMm: number,
    range: DvfDisplayRangeMm
): number {
    const span = range.highMm - range.lowMm;
    const t = span > 0 ? (magnitudeMm - range.lowMm) / span : 0;
    return Math.max(0, Math.min(N_BUCKETS - 1, Math.floor(t * N_BUCKETS)));
}

export function bucketStopsBetween(fromPct: number, toPct: number): string[] {
    const width = (toPct - fromPct) / N_BUCKETS;
    // Unrounded, so each band edge falls exactly where its bucket begins.
    return BUCKET_COLORS.flatMap((color, k) => [
        `${color} ${fromPct + width * k}%`,
        `${color} ${fromPct + width * (k + 1)}%`,
    ]);
}

export function rampGradientCss(
    range: DvfDisplayRangeMm,
    maxMagnitudeMm: number
): string {
    const lowPct =
        maxMagnitudeMm > 0 ? (range.lowMm / maxMagnitudeMm) * 100 : 0;
    const highPct =
        maxMagnitudeMm > 0 ? (range.highMm / maxMagnitudeMm) * 100 : 100;
    return [
        'linear-gradient(to right',
        `${OUTSIDE_RANGE_COLOR} 0%`,
        `${OUTSIDE_RANGE_COLOR} ${lowPct}%`,
        ...bucketStopsBetween(lowPct, highPct),
        `${OUTSIDE_RANGE_COLOR} ${highPct}%`,
        `${OUTSIDE_RANGE_COLOR} 100%)`,
    ].join(', ');
}

export function isOutsideDisplayRange(
    magnitudeMm: number,
    range: DvfDisplayRangeMm
): boolean {
    return magnitudeMm < range.lowMm || magnitudeMm > range.highMm;
}

const OUTSIDE_RANGE_COLOR = 'rgb(150, 150, 150)';

export interface ProjectedGlyph {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    bucket: number;
    isDot: boolean;
    isLengthClamped: boolean;
    isOutsideRange: boolean;
}

function drawHead(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    ang: number,
    len: number
) {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - len * Math.cos(ang - 0.4), y - len * Math.sin(ang - 0.4));
    ctx.lineTo(x - len * Math.cos(ang + 0.4), y - len * Math.sin(ang + 0.4));
    ctx.closePath();
    ctx.fill();
}

export function latticeEdgeIndices(
    uCount: number,
    vCount: number
): [number, number][] {
    const pairs: [number, number][] = [];
    for (let l = 0; l < vCount; l++) {
        for (let k = 0; k < uCount; k++) {
            const i = l * uCount + k;
            if (k + 1 < uCount) {
                pairs.push([i, i + 1]);
            }
            if (l + 1 < vCount) {
                pairs.push([i, i + uCount]);
            }
        }
    }
    return pairs;
}

export interface ProjectedEdge {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

const GRID_COLOR = '#ffffff';

export function drawEdges(
    ctx: CanvasRenderingContext2D,
    edges: ProjectedEdge[]
) {
    ctx.lineWidth = 1;
    // One path for all edges: ~20k segments per slice is too many to stroke() each.
    ctx.globalAlpha = 0.65;
    ctx.strokeStyle = GRID_COLOR;
    ctx.beginPath();
    for (const e of edges) {
        ctx.moveTo(e.x1, e.y1);
        ctx.lineTo(e.x2, e.y2);
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
}

export function drawGlyphs(
    ctx: CanvasRenderingContext2D,
    glyphs: ProjectedGlyph[]
) {
    for (const g of glyphs) {
        const color = g.isOutsideRange
            ? OUTSIDE_RANGE_COLOR
            : BUCKET_COLORS[g.bucket];
        const alpha = g.isOutsideRange ? 0.35 : 1;
        if (g.isDot) {
            ctx.globalAlpha = 0.6 * alpha;
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(g.x1, g.y1, 1, 0, Math.PI * 2);
            ctx.fill();
            continue;
        }
        ctx.globalAlpha = 0.95 * alpha;
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(g.x1, g.y1);
        ctx.lineTo(g.x2, g.y2);
        ctx.stroke();
        const ang = Math.atan2(g.y2 - g.y1, g.x2 - g.x1);
        const hl = 3.5;
        drawHead(ctx, g.x2, g.y2, ang, hl);
        if (g.isLengthClamped) {
            // A trailing chevron so an off-the-scale arrow reads as ">>", not full-scale.
            ctx.beginPath();
            ctx.moveTo(g.x2 - hl * Math.cos(ang), g.y2 - hl * Math.sin(ang));
            ctx.lineTo(
                g.x2 - 2 * hl * Math.cos(ang - 0.4),
                g.y2 - 2 * hl * Math.sin(ang - 0.4)
            );
            ctx.moveTo(g.x2 - hl * Math.cos(ang), g.y2 - hl * Math.sin(ang));
            ctx.lineTo(
                g.x2 - 2 * hl * Math.cos(ang + 0.4),
                g.y2 - 2 * hl * Math.sin(ang + 0.4)
            );
            ctx.stroke();
        }
    }
    ctx.globalAlpha = 1;
}
